import {action, makeAutoObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {TextInputChangeEventAction} from '../../../../../../uikit/TextInput/types';
import type {Result} from './types';

import {MetricModel} from '../../../../../../core/domain/Metric/Metric.model';
import {
  limitFloatFractionLengthFactory,
  limitNumberLengthFactory,
  replaceAllButFloat,
} from '../../../../../../utils/format';
import {logger} from '../../../../../../utils/logger';
import {isDefined} from '../../../../../../utils/typecheck';
import {
  createTextInputField,
  type TextInputField,
} from '../../../../../../utils/validation';

export class AddWeightStore {
  field: TextInputField;
  selectedUnit: MetricModel.WeightUnit;
  private MAX_WEIGHT = 1000;
  private MIN_WEIGHT = 0;

  constructor(private readonly props: AddWeightStore.InitialProps = {}) {
    this.selectedUnit = props.defaultUnit ?? 'lbs';
    this.field = this.createField();

    makeAutoObservable(this, {}, {autoBind: true});
  }

  get currentUnitLabel() {
    return this.getUnitLabel(this.selectedUnit);
  }

  get isValid() {
    return Boolean(this.field.value.length && this.field.isValid);
  }

  get options() {
    const units: MetricModel.WeightUnit[] = ['lbs', 'kg'];

    return units.map(unit => ({
      isSelected: unit === this.selectedUnit,
      label: this.getUnitLabel(unit),
      value: unit,
    }));
  }

  get result(): Result {
    return {
      unit: this.selectedUnit,
      weight: parseFloat(this.field.value),
    };
  }

  adjustWeight(delta: number) {
    const current = parseFloat(this.field.value);
    const value = Number.isFinite(current) ? current : this.MIN_WEIGHT;

    this.setWeight(value + delta);
  }

  changeUnit(unit: MetricModel.WeightUnit) {
    if (unit === this.selectedUnit) {
      return;
    }

    const currentValue = parseFloat(this.field.value);
    const prevUnit = this.selectedUnit;

    this.setSelectedUnit(unit);

    if (!Number.isFinite(currentValue)) {
      return;
    }

    const weightKg =
      prevUnit === 'lbs'
        ? MetricModel.poundsToKilogram(currentValue)
        : currentValue;
    const converted =
      unit === 'lbs'
        ? parseFloat((weightKg * MetricModel.poundsInKilogram).toFixed(1))
        : parseFloat(weightKg.toFixed(1));

    this.setWeight(converted);
  }

  getUnitLabel(unit: MetricModel.WeightUnit) {
    /**
     * @i18n_parser
     *
     * t('shared:lbs')
     * t('shared:kg')
     */
    return i18n.t(`shared:${unit}`);
  }

  onWeightChange(actionObj: TextInputChangeEventAction) {
    const {payload} = actionObj;

    if (isDefined(payload?.text)) {
      let text = replaceAllButFloat(payload!.text);

      const limitQuantity = limitNumberLengthFactory(
        this.MAX_WEIGHT.toString().length,
      );
      const limitFraction = limitFloatFractionLengthFactory(1);

      text = limitQuantity(limitFraction(text));

      actionObj.payload = {text};

      this.field.onChange?.(actionObj);
    }
  }

  setSelectedUnit(unit: MetricModel.WeightUnit) {
    logger.event('add_weight_select_units_tap', {value: unit});

    this.selectedUnit = unit;
  }

  setWeight(value: number) {
    if (!Number.isFinite(value)) {
      this.field.value = this.MIN_WEIGHT.toFixed(1);
      this.field.validate();

      return;
    }

    const clamped = Math.max(this.MIN_WEIGHT, Math.min(this.MAX_WEIGHT, value));
    const text = clamped.toFixed(1);

    this.field.value = text;
    this.field.validate();
  }

  private createField() {
    const defaultWeight =
      this.props.defaultWeight ?? (this.selectedUnit === 'lbs' ? 165 : 70);

    return createTextInputField('weight', {
      customValidate: action(value => {
        let isValid = true;
        let error = '';

        const floatValue = parseFloat(value);

        switch (true) {
          case value === '':
            error = i18n.t('shared:weight_required');
            isValid = false;
            break;
          case floatValue < this.MIN_WEIGHT:
            error = i18n.t('shared:min_quantity', {
              value: this.MIN_WEIGHT,
            });
            isValid = false;
            break;
          case floatValue > this.MAX_WEIGHT:
            error = i18n.t('shared:max_quantity', {
              value: this.MAX_WEIGHT,
            });
            isValid = false;
            break;
        }

        this.field.error = error;

        return isValid;
      }),
      defaultValue: defaultWeight.toString(),
      validationRule: {
        type: 'string',
      },
    });
  }
}

export namespace AddWeightStore {
  export type InitialProps = {
    defaultUnit?: MetricModel.WeightUnit;
    defaultWeight?: number;
  };
}
